import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import Rate from '@/models/Products/Rates/Rate';
import RateSource from '@/models/Products/RateSources/RateSource';
import CreateRateSourceRequest from '@/models/Products/RateSources/CreateRateSourceRequest';
import UpdateRateSourceRequest from '@/models/Products/RateSources/UpdateRateSourceRequest';
import CreateRateRequest from '@/models/Products/Rates/CreateRateRequest';
import UpdateRateRequest from '@/models/Products/Rates/UpdateRateRequest';
import GetHistoryResponse from '@/models/Shared/GetHistoryResponse';
import HistoryEntry from '@/models/Shared/HistoryEntry';
import PermissionUtils from '@/lib/permissionUtils';

export interface IRateSourceState {
    loading: boolean;
    loadingRates: boolean;
    loadingDialog: boolean;
    loadingHistory: boolean;
    rateSources: RateSource[];
    rateSourcesForDialog: RateSource[];
    rateSource: RateSource;
    totalRateSourcesPages: number;
    newRateSourceId: string;
    rates: Rate[];
    totalRatesPages: number;
    history: HistoryEntry[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'RateSourceModule', namespaced: true })
class RateSourceStore extends VuexModule implements IRateSourceState {

    public loading: boolean = true;
    public loadingRates: boolean = true;
    public loadingDialog: boolean = false;
    public loadingHistory: boolean = false;
    public rateSources: RateSource[] = [];
    public rateSourcesForDialog: RateSource[] = [];
    public rateSource: RateSource = new RateSource();
    public totalRateSourcesPages: number = 1;
    public rates: Rate[] = [];
    public totalRatesPages: number = 1;
    public newRateSourceId: string = '';
    public history: HistoryEntry[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getRateSources(pageNumber: number) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRateSources", response.data.results);
            this.context.commit("setRateSourcesTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getRateSourcesForDialog() {

        // This action is specifically for the edit/create dialogs where we need to ignore the normal API pagination
        // logic in getRateSources and instead get all rate sources so they can be listed in the drop down menu for dialogs
        this.setLoadingDialog(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources`)
        .then(response => {
            this.context.commit("setRateSourcesForDialog", response.data.results);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getRateSource(id: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources/${id}`)
        .then(response => {
            this.context.commit("setRateSource", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async createRateSource(request: CreateRateSourceRequest) {

        this.setLoadingDialog(true);
        this.context.commit("setRateSourceFromCreate", null);

        await ajax.post(`/portal-backend/fi-api/internal/rate-sources`, request)
        .then(response => {
            this.context.commit("setRateSourceFromCreate", response.data.id);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editRateSourceDetails(request: UpdateRateSourceRequest) {

        this.setLoadingDialog(true);

        await ajax.patch(`/portal-backend/fi-api/internal/rate-sources/${this.rateSource.id}`, request)
        .then(response => {
            this.context.commit("setRateSource", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getRatesForRateSource() {

        this.setLoadingRates(true);

        await ajax.get(`/portal-backend/fi-api/internal/rates?rateSourceId=${this.rateSource.id}`)
        .then(response => {
            this.context.commit("setRates", response.data.results);
            this.context.commit("setRatesTotalPages", response.data.records.totalRecords);
            this.setLoadingRates(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingRates(false);
        });
    }

    @Action
    async createRate(request: CreateRateRequest) {

        this.setLoadingDialog(true);
        request.rateSourceId = this.rateSource.id;

        await ajax.post(`/portal-backend/fi-api/internal/rates`, request)
        .then(response => {
            this.context.commit("setRateFromCreate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async editRateDetails(request: UpdateRateRequest) {

        this.setLoadingDialog(true);
        const rateId = request.rateId;
        request.rateId = undefined;
        request.updatedAt = undefined;

        await ajax.patch(`/portal-backend/fi-api/internal/rates/${rateId}`, request)
        .then(response => {
            this.context.commit("setRateFromEdit", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getHistory() {

        this.setLoadingHistory(true);

        await ajax.get(`/portal-backend/fi-api/internal/rate-sources/${this.rateSource.id}/history`)
        .then(response => {
            this.context.commit("setHistory", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingRates = true;
        this.loadingDialog = false;
        this.loadingHistory = true;
        this.rateSources = [];
        this.rateSourcesForDialog = [];
        this.rateSource = new RateSource();
        this.totalRateSourcesPages = 1;
        this.rates = [];
        this.totalRatesPages = 1;
        this.newRateSourceId = '';
        this.history = [];
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingRates(loading: boolean) {
        this.loadingRates = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingHistory(loading: boolean) {
        this.loadingHistory = loading;
    }

    @Mutation
    private async setRateSources(response: RateSource[]) {
        if (response) {
            this.rateSources = response;
        }
    }

    @Mutation
    private async setRateSourcesForDialog(response: RateSource[]) {
        if (response) {
            this.rateSourcesForDialog = response;
        }
    }

    @Mutation
    private async setRateSource(response: RateSource) {
        this.rateSource = response;
    }

    @Mutation
    private async setRateSourcesTotalPages(total: number) {
        this.totalRateSourcesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRateSourceFromCreate(id: string) {
        this.newRateSourceId = id;
    }

    @Mutation
    private async setRates(response: Rate[]) {
        if (response) {
            this.rates = response;
        }
    }

    @Mutation
    private async setRatesTotalPages(total: number) {
        this.totalRatesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRateFromCreate(rate: Rate) {
        this.rates.unshift(rate);
    }

    @Mutation
    private async setRateFromEdit(rate: Rate) {
        const index = this.rates.findIndex((findRate: Rate) => rate.id === rate.id);
        this.rates.splice(index, 1, rate);
    }

    @Mutation
    private async setHistory(response: GetHistoryResponse) {
        this.history = response.results;
    }

}

export const RateSourceModule = getModule(RateSourceStore);